// Canvas drawing and rendering utilities for DiagnoseOverlays

export const THROTTLE_MS = 32; // ~30fps
export const MAX_PATHS_IN_POOL = 1000;
export const POINTS_THRESHOLD = 200; // Maximum points per contour

// Optimize points by reducing density if too many
export const optimizePoints = (points) => {
  if (!points || points.length <= POINTS_THRESHOLD) return points;

  const step = Math.ceil(points.length / POINTS_THRESHOLD);
  return points.filter((_, index) => index % step === 0);
};

// Get or create path from pool
export const getPathFromPool = (points, imgSize, pathPool) => {
  const key = points.map((p) => `${p[0]},${p[1]}`).join('|');

  if (pathPool.current.has(key)) {
    return pathPool.current.get(key);
  }

  // Clear old paths if pool is too large
  if (pathPool.current.size >= MAX_PATHS_IN_POOL) {
    const firstKey = pathPool.current.keys().next().value;
    pathPool.current.delete(firstKey);
  }

  const path = new Path2D();
  const optimizedPoints = optimizePoints(points);
  const x0 = optimizedPoints[0][0] * imgSize[0];
  const y0 = optimizedPoints[0][1] * imgSize[1];
  path.moveTo(x0, y0);

  for (let i = 1; i < optimizedPoints.length; i++) {
    const x = optimizedPoints[i][0] * imgSize[0];
    const y = optimizedPoints[i][1] * imgSize[1];
    path.lineTo(x, y);
  }
  path.closePath();

  pathPool.current.set(key, path);
  return path;
};

// Draw shape function
export const drawShape = (points, context, color, subtract, fill, imgSize, pathPool) => {
  if (!points?.length) return;

  try {
    context.globalCompositeOperation = subtract ? 'destination-out' : 'source-over';
    const path = getPathFromPool(points, imgSize, pathPool);

    if (fill) {
      context.fillStyle = color;
      context.fill(path);
    } else {
      context.lineWidth = 10;
      context.strokeStyle = color;
      context.stroke(path);
    }
  } catch (error) {
    console.error('Error in drawShape:', error);
  }
};

// Smooth contour drawing using quadratic Bézier curves
export const drawSmoothShape = (points, context, color, subtract, fill, imgSize, pathPool) => {
  if (!points?.length || points.length < 3) {
    // Fall back to regular drawing for simple shapes
    return drawShape(points, context, color, subtract, fill, imgSize, pathPool);
  }

  try {
    context.globalCompositeOperation = subtract ? 'destination-out' : 'source-over';

    // Create a new path for smooth drawing
    const path = new Path2D();

    // Convert normalized coordinates to pixel coordinates
    const pixelPoints = points.map((point) => [point[0] * imgSize[0], point[1] * imgSize[1]]);

    // Start the path
    path.moveTo(pixelPoints[0][0], pixelPoints[0][1]);

    // Use quadratic Bézier curves for smooth lines
    for (let i = 1; i < pixelPoints.length - 1; i++) {
      const current = pixelPoints[i];
      const next = pixelPoints[i + 1];

      // Calculate control point (midpoint between current and next)
      const controlX = (current[0] + next[0]) / 2;
      const controlY = (current[1] + next[1]) / 2;

      // Draw quadratic curve to control point, then to next point
      path.quadraticCurveTo(current[0], current[1], controlX, controlY);
    }

    // Connect to the last point
    const lastPoint = pixelPoints[pixelPoints.length - 1];
    path.lineTo(lastPoint[0], lastPoint[1]);

    // Close the path
    path.closePath();

    if (fill) {
      context.fillStyle = color;
      context.fill(path);
    } else {
      context.lineWidth = 14;
      context.strokeStyle = color;
      context.stroke(path);
    }
  } catch (error) {
    console.error('Error in drawSmoothShape:', error);
    // Fall back to regular drawing
    drawShape(points, context, color, subtract, fill, imgSize, pathPool);
  }
};

// Alternative smooth drawing using Catmull-Rom splines
export const drawSmoothShapeCatmullRom = (points, context, color, subtract, fill, imgSize, pathPool) => {
  if (!points?.length || points.length < 4) {
    // Fall back to regular drawing for simple shapes
    return drawShape(points, context, color, subtract, fill, imgSize, pathPool);
  }

  try {
    context.globalCompositeOperation = subtract ? 'destination-out' : 'source-over';

    const path = new Path2D();

    // Convert normalized coordinates to pixel coordinates
    const pixelPoints = points.map((point) => [point[0] * imgSize[0], point[1] * imgSize[1]]);

    // Catmull-Rom spline implementation
    const tension = 0.5; // Controls the tightness of the curve

    // Start the path
    path.moveTo(pixelPoints[0][0], pixelPoints[0][1]);

    // Generate smooth curve points
    const numSegments = 1; // Number of points between each original point
    for (let i = 0; i < pixelPoints.length - 1; i++) {
      const p0 = i > 0 ? pixelPoints[i - 1] : pixelPoints[0];
      const p1 = pixelPoints[i];
      const p2 = pixelPoints[i + 1];
      const p3 = i < pixelPoints.length - 2 ? pixelPoints[i + 2] : pixelPoints[pixelPoints.length - 1];

      for (let j = 1; j <= numSegments; j++) {
        const t = j / numSegments;
        const t2 = t * t;
        const t3 = t2 * t;

        // Catmull-Rom matrix coefficients
        const c0 = -tension * t3 + 2 * tension * t2 - tension * t;
        const c1 = (2 - tension) * t3 + (tension - 3) * t2 + 1;
        const c2 = (tension - 2) * t3 + (3 - 2 * tension) * t2 + tension * t;
        const c3 = tension * t3 - tension * t2;

        const x = c0 * p0[0] + c1 * p1[0] + c2 * p2[0] + c3 * p3[0];
        const y = c0 * p0[1] + c1 * p1[1] + c2 * p2[1] + c3 * p3[1];

        path.lineTo(x, y);
      }
    }

    // Close the path
    path.closePath();

    if (fill) {
      context.fillStyle = color;
      context.fill(path);
    } else {
      context.lineWidth = 14;
      context.strokeStyle = color;
      context.stroke(path);
    }
  } catch (error) {
    console.error('Error in drawSmoothShapeCatmullRom:', error);
    // Fall back to regular drawing
    drawShape(points, context, color, subtract, fill, imgSize, pathPool);
  }
};

// Draw all shapes function with batching
export const drawAllShapes = (
  overlayData,
  context,
  color,
  show,
  lesionModule,
  fill,
  tabsIndex,
  imgSize,
  pathPool
) => {
  // Early return if show is false or no overlay data - consistent with bounding box logic
  if (!show || !overlayData || !Array.isArray(overlayData) || overlayData.length === 0) {
    return;
  }

  try {
    // Process contours first - only if show is true
    overlayData.forEach((overlay) => {
      if (overlay && overlay.contours && Array.isArray(overlay.contours)) {
        overlay.contours.forEach((contour) => {
          if (contour && Array.isArray(contour) && contour.length > 0) {
            drawSmoothShapeCatmullRom(contour, context, color, false, fill, imgSize, pathPool);
          }
        });
      }
    });

    // Then process boxes if needed - only if show is true and tabsIndex is 0
    if (tabsIndex === 0 && show) {
      overlayData.forEach((overlay) => {
        if (overlay && overlay.box && Array.isArray(overlay.box) && overlay.box.length === 4) {
          drawSingleBoxOnCanvas(overlay.box, context, color, lesionModule, show, imgSize);
        }
      });
    }
  } catch (error) {
    console.error('Error in drawAllShapes:', error);
  }
};
